import SelectionState from "state/selections/SelectionState";
import { selectDefinitions } from "./selectors";
import { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { titleCase } from "@ls/js-utils";
import { deleteColumn } from "state/grid/actions";
import { useQuery } from "react-query";
import {
  selectCommittedEntities,
  selectSelectedEntityIds,
} from "state/selections/selector";
import { selectScenarioEntityIds } from "state/scenario/selector";
import Mode from "state/selections/Mode";
import { format } from "date-fns";
import scope from "./scope";
import { useQueryEnabled } from "state/selections/hooks";

export const useDataQuery = () => {
  const definitions = useSelector(selectDefinitions(SelectionState.COMMITTED));
  const selectedEntityIds = useSelector(
    selectSelectedEntityIds(SelectionState.COMMITTED)
  );
  const scenarioEntityIds = useSelector(selectScenarioEntityIds);
  const queryEnabled = useQueryEnabled();
  const { asOfDateEntity, groupTypeEntity, groupEntities, modeEntity } =
    useSelector(selectCommittedEntities);
  return useQuery({
    enabled: definitions?.length > 0 && queryEnabled,
    queryKey: [
      "cashflow",
      definitions,
      selectedEntityIds,
      scenarioEntityIds,
      asOfDateEntity,
      groupTypeEntity,
      groupEntities,
      modeEntity,
    ],
    queryFn: async () => {
      let groupType;
      let identifier;
      switch (modeEntity.value) {
        default:
          throw Error(`Unknown mode ${modeEntity.value}`);
        case Mode.ACC_GROUPS:
          groupType = groupTypeEntity.groupingValue;
          identifier = groupEntities
            .map((groupEntity) => groupEntity[groupTypeEntity.groupingKey])
            .join(",");
          break;
        case Mode.ENTITIES:
          groupType = "entity_list";
          identifier = selectedEntityIds?.join(",");
          break;
        case Mode.SCENARIOS:
          groupType = "entity_list";
          identifier = scenarioEntityIds?.join(",");
          break;
      }
      const url = new URL(
        `${process.env.REACT_APP_BASE_URL_TRADE_HISTORY_SERVICE}/rest/trades/cashflowsByGroupType`
      );
      url.searchParams.append("majorMinorAdjustment", "MINOR");
      url.searchParams.append("groupType", groupType);
      url.searchParams.append("identifier", identifier);
      url.searchParams.append("asOfDate", format(asOfDateEntity, "MM/dd/yyyy"));
      const response = await window.fetch(url.toString());
      return await response.json();
    },
  });
};

export const useRowsMap = () => {
  const { data } = useDataQuery();
  return useMemo(() => {
    const rowsMap = {};
    if (data?.length) {
      data.forEach((cashFlowData) => {
        const { entityId } = cashFlowData;
        rowsMap[entityId] = {
          [scope]: cashFlowData,
        };
      });
    }
    return rowsMap;
  }, [data]);
};

export const useColDefs = () => {
  const definitions = useSelector(selectDefinitions(SelectionState.COMMITTED));
  const { isFetching } = useDataQuery();
  const dispatch = useDispatch();
  return useMemo(() => {
    return definitions.reduce((colDefs, definition) => {
      const { period, type } = definition;
      colDefs.push({
        headerName: `${titleCase(period)}, ${titleCase(type)}`,
        field: `${scope}.${period}.${type}`,
        colId: `${period}_${type}`,
        loading: isFetching,
        X_onDelete: () => dispatch(deleteColumn(definition)),
      });
      return colDefs;
    }, []);
  }, [definitions, dispatch, isFetching]);
};
